import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFiles } from '../utils/api';
import './UploadFiles.css';

// Document categories configuration
const DOCUMENT_CATEGORIES = [
  { id: 'personal', title: 'Personal', icon: '🆔', accept: 'image/*,.pdf' },
  { id: 'employment', title: 'Employment', icon: '💼', accept: 'image/*,.pdf' },
  { id: 'photo', title: 'Passport Photo', icon: '📸', accept: 'image/*' },
  { id: 'education', title: 'Education', icon: '📚', accept: '.pdf,image/*' },
  { id: 'certificates', title: 'Certificates', icon: '🎓', accept: '.pdf,image/*' },
];

const UploadFiles = () => {
  const [filesByCategory, setFilesByCategory] = useState({});
  const [dragActiveCategory, setDragActiveCategory] = useState(null);
  const [uploadStatusByCategory, setUploadStatusByCategory] = useState({});
  const [uploadStatusTypeByCategory, setUploadStatusTypeByCategory] = useState({}); // 'success' or 'error'
  const [uploadingByCategory, setUploadingByCategory] = useState({});
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const navigate = useNavigate();

  const userEmail = localStorage.getItem('userEmail') || 'User';

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    navigate('/login');
  };

  const handleDrag = (e, categoryId) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActiveCategory(categoryId);
    } else if (e.type === 'dragleave') {
      setDragActiveCategory(null);
    }
  };

  const handleDrop = (e, categoryId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveCategory(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files, categoryId);
    }
  };

  const handleFileInput = (e, categoryId) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files, categoryId);
    }
  };

  const handleFiles = (fileList, categoryId) => {
    const newFiles = Array.from(fileList).map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      category: categoryId,
      status: 'pending'
    }));
    
    setFilesByCategory(prev => ({
      ...prev,
      [categoryId]: [...(prev[categoryId] || []), ...newFiles]
    }));
  };

  const removeFile = (categoryId, fileId) => {
    setFilesByCategory(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter(file => file.id !== fileId)
    }));
  };

  const handleUploadCategory = async (categoryId) => {
    const categoryFiles = filesByCategory[categoryId];
    
    if (!categoryFiles || categoryFiles.length === 0) {
      setUploadStatusByCategory(prev => ({
        ...prev,
        [categoryId]: 'Please select at least one file to upload'
      }));
      setUploadStatusTypeByCategory(prev => ({
        ...prev,
        [categoryId]: 'error'
      }));
      return;
    }

    setUploadingByCategory(prev => ({ ...prev, [categoryId]: true }));
    setUploadStatusByCategory(prev => ({ ...prev, [categoryId]: 'Uploading...' }));
    setUploadStatusTypeByCategory(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });

    try {
      const response = await uploadFiles(categoryFiles, categoryId);
      
      // Show response in modal
      setResponseData(response.uploaded_files);
      setShowResponseModal(true);
      
      // Update file statuses for this category
      setFilesByCategory(prev => ({
        ...prev,
        [categoryId]: prev[categoryId].map(file => ({ ...file, status: 'uploaded' }))
      }));
      
      // Set success message and type
      setUploadStatusByCategory(prev => ({
        ...prev,
        [categoryId]: response.message || 'Files uploaded successfully!'
      }));
      setUploadStatusTypeByCategory(prev => ({
        ...prev,
        [categoryId]: 'success'
      }));
      
      // Clear files after successful upload
      setTimeout(() => {
        setUploadStatusByCategory(prev => {
          const updated = { ...prev };
          delete updated[categoryId];
          return updated;
        });
        setUploadStatusTypeByCategory(prev => {
          const updated = { ...prev };
          delete updated[categoryId];
          return updated;
        });
        setFilesByCategory(prev => {
          const updated = { ...prev };
          delete updated[categoryId];
          return updated;
        });
      }, 3000);
    } catch (err) {
      // Set error message and type
      setUploadStatusByCategory(prev => ({
        ...prev,
        [categoryId]: err.message || 'Upload failed. Please try again.'
      }));
      setUploadStatusTypeByCategory(prev => ({
        ...prev,
        [categoryId]: 'error'
      }));
      setFilesByCategory(prev => ({
        ...prev,
        [categoryId]: prev[categoryId].map(file => ({ ...file, status: 'error' }))
      }));
    } finally {
      setUploadingByCategory(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <div>
          <h1 className="upload-title">Document Upload</h1>
          <p className="upload-subtitle">Welcome, {userEmail}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/profile')} className="profile-button">
            👤 Profile
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="upload-content">
        <div className="document-sections">
          {DOCUMENT_CATEGORIES.map((category) => (
            <div key={category.id} className="document-section">
              <div className="section-header">
                <span className="section-icon">{category.icon}</span>
                <h3 className="section-title">{category.title}</h3>
              </div>
              
              <div
                className={`upload-area compact ${dragActiveCategory === category.id ? 'drag-active' : ''}`}
                onDragEnter={(e) => handleDrag(e, category.id)}
                onDragLeave={(e) => handleDrag(e, category.id)}
                onDragOver={(e) => handleDrag(e, category.id)}
                onDrop={(e) => handleDrop(e, category.id)}
              >
                <div className="upload-icon-small">📁</div>
                <p className="upload-text">Drag & Drop or</p>
                <label htmlFor={`file-input-${category.id}`} className="browse-button-small">
                  Browse
                </label>
                <input
                  id={`file-input-${category.id}`}
                  type="file"
                  multiple
                  accept={category.accept}
                  onChange={(e) => handleFileInput(e, category.id)}
                  className="file-input"
                />
              </div>

              {filesByCategory[category.id] && filesByCategory[category.id].length > 0 && (
                <div className="section-files">
                  <div className="files-container-section">
                    {filesByCategory[category.id].map((fileItem) => (
                      <div key={fileItem.id} className="file-item-compact">
                        <div className="file-info">
                          <span className="file-name">{fileItem.name}</span>
                          <span className="file-size">{formatFileSize(fileItem.size)}</span>
                        </div>
                        <button
                          onClick={() => removeFile(category.id, fileItem.id)}
                          className="remove-button-small"
                          title="Remove file"
                          disabled={uploadingByCategory[category.id]}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="section-actions">
                    <button 
                      onClick={() => handleUploadCategory(category.id)} 
                      className="upload-button-section"
                      disabled={uploadingByCategory[category.id]}
                    >
                      {uploadingByCategory[category.id] ? 'Uploading...' : 'Upload'}
                    </button>
                    <button 
                      onClick={() => {
                        setFilesByCategory(prev => {
                          const updated = { ...prev };
                          delete updated[category.id];
                          return updated;
                        });
                      }}
                      className="clear-button-section"
                      disabled={uploadingByCategory[category.id]}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
              
              {uploadStatusByCategory[category.id] && (
                <div className={`upload-status-section ${uploadStatusTypeByCategory[category.id] || ''}`}>
                  {uploadStatusTypeByCategory[category.id] === 'success' && '✅ '}
                  {uploadStatusTypeByCategory[category.id] === 'error' && '❌ '}
                  {uploadStatusByCategory[category.id]}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Response</h2>
              <button className="modal-close" onClick={() => setShowResponseModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <pre className="json-display">
                {JSON.stringify(responseData, null, 2)}
              </pre>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={() => setShowResponseModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFiles;

